import _ from "underscore";
import DriveView from "../../DriveView";
import { getMinEwdWorkMaximum } from "../../utils/helpers";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import Notifications from "../../utils/notifications";
import LockProgressBar from "./lockProgressBar";
import { show } from "../../utils/vanilla";
import HosProcess, { IHosProcessResult } from "../../services/hosProcess";
import {
    type IHosAvailability, type IWorkdaySummary, type IViolation, convertDecimalToTimeString, getMinEwdTimeUntilMustRest,
    getMinAvailability, getIconForAvailability, availToHours, getLatestWorkday, timeSpanToMs, HOURS,
    type IEwdRulesetWorkRestConfig,
    type IHosRulesetConfig,
    type IEwdWorkRestAvailability
} from "hos-tools";
import { handleJSNative } from "../../utils/error";
import { IMobilePageState, PageLevel } from "../../store/mobile/types";
import { MobileActions } from "../../store/mobile/actions";
import DutyStatusLog from "../../model/DutyStatusLog";
import pageHTML from "./lock.tmpl";
import { FeatureFlagName } from "../../utils/featureFlagName";
import { getSplitSleeperAvailability, isSplitSleeperToggleAvailable } from "../../modules/hos/helpers/splitSleeperAvailability";
import { getSplitSleeperEnabled } from "../../modules/hos/store/selectors";

    const FIFTEEN_MINUTES = 15 * 1000 * 60;
    const SEVEN_HOURS = 7 * 1000 * 60 * 60;
    const TWENTYFOUR_HOURS = 24 * 1000 * 60 * 60;

    export default class LockView extends DriveView<Backbone.Model> {

        protected strings = [
            "{0} left",
            "personal conveyance",
            "yard move",
            "Oil well wait time",
            "You are currently under the {0} exemption.",
            "h",
            "{minutes}m",
            "{0} break in",
            "{0} rest in",
            "{0}-night"
        ];
        private readonly template = _.template(pageHTML);
        private readonly hosUtils: HosProcess;
        private readonly isEWD: boolean;
        private currentUser: DeepReadonly<IUser> | null;
        private logCollection: DutyStatusLog[];
        private progressBarCollection: LockProgressBar[] = [];
        private ruleResult: IHosProcessResult | undefined;
        private previousPageState: IMobilePageState | undefined;
        private hasNewLogNeedingManualLocation = false;

        constructor (options) {
            super(options, {
                title: "Driving",
                pageLevel: PageLevel.ONE,
                isLocked: true
            });

            this.currentUser = this.app.userService.getCurrentUser();
            this.hosUtils = this.app.hosUtils;
            this.isEWD = this.app.store.getState().data.ELDJurisdiction === "EWD";

            if (window.history.pushState) {
                window.history.pushState({ lock: true }, "");
                window.addEventListener("popstate", this.popstate);
            }

            this.updateLogCollection();

            this.listenTo(dispatcher, DispatcherEvents.SyncUserHosRuleSet, this.onUserRulesetSync.bind(this));
            this.listenTo(dispatcher, DispatcherEvents.HosRuleCompute, this.ruleCompute.bind(this));
            this.listenTo(dispatcher, DispatcherEvents.SyncHosLocationManual, this.promptDutyStatusAddresses.bind(this));
            this.listenTo(dispatcher, DispatcherEvents.SyncComplete, () => {
                this.updateLogCollection();
                this.render();
            });
            this.hosUtils.process();

            // Locked class in the body triggers other elements to be hidden via CSS
            document.body.classList.add("locked");
        }

        render () {
            let currentStatus = this.app.dutyStatusLogService.getCurrentDriverStatus(this.logCollection);
            let currentSituationDesc = this.app.dutyStatusLogService.getCurrentSituationDescription(currentStatus, this.logCollection);
            let userRuleSet = this.hosUtils.getDriverRuleSet();
            let noAvailabilityBar = this.hosUtils.noAvailabilityBar(userRuleSet);
            let userRuleSetName = userRuleSet.name;
            let defaultAvailability = this.hosUtils.getDefaultAvailability(userRuleSetName);
            let displayAvailability = this.ruleResult ? this.getAvailabilityForDisplay(this.ruleResult).availability : undefined;
            let isAdverseDrivingApplied = displayAvailability?.isAdverseDrivingApplied;
            let showCurrentStatus = this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.ENABLE_CURRENT_STATUS_ON_LOCKSCREEN);
            let templateObj = {
                availabilityPercent: 100,
                violations: <IViolation[]>[],
                inViolation: false,
                availability: defaultAvailability as any,
                minimumAvailability: "0:00",
                currentSituationDesc,
                hasCriticalDefects: false,
                ruleSetName: "",
                noAvailabilityBar,
                currentStatus: "",
                minimumAvailabilityText: "",
                hasNewLogNeedingManualLocation: this.hasNewLogNeedingManualLocation,
                minimumAvailabilityIcon: "",
                isEWD: false,
                isAdverseWeatherApplied: isAdverseDrivingApplied,
                isEmergencyResponseExemptionApplied: displayAvailability?.isEmergencyResponseExemptionApplied === true,
                showCurrentStatus
            };

            if (this.ruleResult && displayAvailability) {
                let minAvail = this.isEWD
                    ? getMinEwdTimeUntilMustRest(this.ruleResult.ewdAvailability || [])
                    : getMinAvailability(displayAvailability);
                let minimumAvailability = minAvail.min;
                let defaultMin = 0;

                if (this.isEWD) {
                    let userRuleSetLabel = this.hosUtils.getValidRuleSet(this.hosUtils.getLatestHosRuleSetIdShortened(true));
                    let rulesetConf = this.hosUtils.getRuleSets()[userRuleSetLabel].config.workRestConfig as IEwdRulesetWorkRestConfig[];
                    defaultMin = getMinEwdWorkMaximum(rulesetConf);
                } else {
                    let defaultAvail = this.hosUtils.getDefaultAvailability(this.hosUtils.getLatestHosRuleSetIdShortened(true));
                    defaultMin = getMinAvailability(defaultAvail).min;
                }

                if (!defaultMin || defaultMin === Infinity) {
                    defaultMin = 8;
                }
                let minimumPercent = (minimumAvailability * 100 / defaultMin);
                templateObj.availabilityPercent = Number(Math.min(minimumPercent, 100).toFixed(1));
                templateObj.minimumAvailability = convertDecimalToTimeString(minimumAvailability)
                    .replace("{0}", this.getString("h"))
                    .replace("{1}", this.getString("{minutes}m").replace("{minutes}", ""));

                if (!this.isEWD) {
                    templateObj.minimumAvailabilityIcon = getIconForAvailability((minAvail as { min: number; name: string }).name);
                }

                templateObj.violations = this.ruleResult.violations;
                templateObj.inViolation = this.ruleResult.inViolation;
            }

            templateObj.hasCriticalDefects = this.currentUser ? !!Notifications.get("dvirCriticalDefects", this.currentUser.id) : false;
            templateObj.ruleSetName = userRuleSetName;
            templateObj.currentStatus = currentStatus;
            templateObj.isEWD = this.isEWD;

            if (templateObj.minimumAvailability !== "") {
                templateObj.minimumAvailabilityText = this.getString("{0} left").replace("{0}", templateObj.minimumAvailability);
            }

            if (currentSituationDesc !== null) {
                templateObj.currentSituationDesc = this.getString(this.strings[4]).replace("{0}", this.getString(currentSituationDesc));
            }

            void this.display(this.template(templateObj))
            .then(() => {
                this.removeAllProgressBars();
                if (this.isEWD) {
                    this.showEwdAvailability();
                } else if (displayAvailability) {
                    this.showAvailability(displayAvailability);
                }
            });

            this.previousPageState = this.app.store.getState().mobile.pageState;
            return this;
        }

        updateLogCollection() {
            this.logCollection = this.app.dutyStatusLogService.getSortedHosLogs(this.app.dutyStatusLogService.getHOSLogs(false).filter(log => log.isStandardStatusDriverLog()));
        }

        remove () {
            document.body.classList.remove("locked");

            this.app.store.dispatch(MobileActions.setPageState({
                title: this.previousPageState ? this.previousPageState.title : this.app.store.getState().mobile.pageState.title,
                pageLevel: this.previousPageState ? this.previousPageState.pageLevel : 1,
                isLocked: false
            }));

            window.removeEventListener("popstate", this.popstate);
            this.removeAllProgressBars();

            // Because we pushed a lock state up in initialize
            !!window.history.pushState && window.history.back();

            super.remove();
            return this;
        }

        private popstate (this: void) {
            window.history.pushState({ lock: true }, "");
        }

        private onUserRulesetSync() {
            this.refreshUserAndRuleset(this.hosUtils.process());
        }

        private ruleCompute(userId: string, ruleResult = this.hosUtils.getRuleResult()) {
            if (userId === this.currentUser?.id) {
                this.refreshUserAndRuleset(ruleResult);
            }
        }

        private refreshUserAndRuleset(ruleResult = this.hosUtils.getRuleResult()) {
            this.currentUser = this.app.userService.getCurrentUser();
            this.ruleResult = ruleResult;
            this.render();
        }

        private promptDutyStatusAddresses() {
            this.hasNewLogNeedingManualLocation = true;
            this.render();
        }

        private isPresent (numberEntity) {
            return numberEntity !== undefined;
        }

        private removeAllProgressBars() {
            this.progressBarCollection.forEach(bar => {
                bar.remove();
            });
            this.progressBarCollection = [];
            let lockBarContainer = document.querySelector(".lock-bars-container");
            lockBarContainer && (lockBarContainer.classList.remove("multi-line"));
        }

        private showLockProgressbar (container: HTMLElement, max: number, min: number, iconClass: string, title: string, className: string, order: number, noTranslate = false): LockProgressBar {
            let lockbar = new LockProgressBar({
                iconClass,
                title,
                container,
                translator: this.translator,
                max,
                min,
                className,
                translatedUnits: [this.getString("h"), this.getString("{minutes}m").replace("{minutes}", "")],
                noTranslate
            });

            container.style.order = `${ order }`;
            show(container);

            lockbar.render().then(() => {
                // Unit test message that a progress bar is rendered
                this.trigger("render-lock-progress-bar", className);
            }).catch(handleJSNative);

            return lockbar;
        }

        private getAvailabilityArray (availability: IHosAvailability): number[] {
            return [
                availability.driving !== undefined ? <number>availToHours(availability.driving) : Infinity,
                availability.cycle !== undefined ? <number>availToHours(availability.cycle) : Infinity,
                availability.rest !== undefined ? <number>availToHours(availability.rest) : Infinity,
                availability.workday !== undefined ? <number>availToHours(availability.workday) : Infinity
            ].sort((a, b) => a - b);
        }

        private isSplitSleeperDisplayEnabled (): boolean {
            if (this.isEWD) {
                return true;
            }

            let ruleSetKey = this.hosUtils.getValidRuleSet(this.hosUtils.getLatestHosRuleSetIdShortened(true));
            let ruleset = this.hosUtils.getRuleSets()[ruleSetKey];
            return !isSplitSleeperToggleAvailable(
                this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.ENABLE_SPLIT_SLEEPER_TOGGLE),
                ruleset
            ) || getSplitSleeperEnabled(
                this.app.store.getState().hosDisplayPreferences,
                this.currentUser?.id
            );
        }

        private getAvailabilityForDisplay (ruleResult: IHosProcessResult) {
            return getSplitSleeperAvailability(ruleResult, this.isSplitSleeperDisplayEnabled());
        }

        private showAvailability (availability: IHosAvailability) {
            if (!this.ruleResult) {
                return;
            }

            let userRuleSetLabel = this.hosUtils.getValidRuleSet(this.hosUtils.getLatestHosRuleSetIdShortened(true));
            let rulesetConf = this.hosUtils.getRuleSets()[userRuleSetLabel].config;
            let workdayInfo = getLatestWorkday(this.ruleResult.workdaySummaries);
            let availabilityArray = this.getAvailabilityArray(availability);
            // The Emergency Response Exemption raises clocks above the rule limits, and a ring cannot show more than its limit
            let ringMax = (limit: number, avail: number): number => availability.isEmergencyResponseExemptionApplied === true ? Math.max(limit, avail) : limit;

            let restContainer = document.getElementById("restLockBar");
            let drivingContainer = document.getElementById("drivingLockBar");
            let workdayContainer = document.getElementById("workdayLockBar");
            let cycleContainer = document.getElementById("cycleLockBar");

            let checkRest = () => rulesetConf.rest && this.isPresent(rulesetConf.rest.betweenRestLimit) && this.isPresent(availability.rest);
            if (restContainer && checkRest()) {
                let avail = availability.rest && timeSpanToMs(availability.rest) || 0;
                let getRestLimit = rulesetConfig => rulesetConfig.rest && rulesetConfig.rest.betweenRestLimit || 0;

                this.progressBarCollection.push(this.showLockProgressbar(
                    restContainer,
                    ringMax(getRestLimit(rulesetConf), avail),
                    avail,
                    "drive-stop",
                    "Break required in",
                    "rest-progress-bar",
                    availabilityArray.indexOf(avail / HOURS)
                ));
            }

            let checkDriving = () => rulesetConf.workday && this.isPresent(rulesetConf.workday.driveMaximum) && this.isPresent(availability.driving);
            if (drivingContainer && checkDriving()) {
                let avail = availability.driving && timeSpanToMs(availability.driving) || 0;
                let getDrivingLimit = (rulesetConfig: DeepReadonly<IHosRulesetConfig>, workdayParams: IWorkdaySummary | undefined): number => {
                    let drivingLimit = rulesetConfig.workday && rulesetConfig.workday.driveMaximum || 0;
                    if (workdayParams && workdayParams.driveExtension) {
                        drivingLimit += timeSpanToMs(workdayParams.driveExtension);
                    }
                    return drivingLimit;
                };

                this.progressBarCollection.push(this.showLockProgressbar(
                    drivingContainer,
                    ringMax(getDrivingLimit(rulesetConf, workdayInfo), avail),
                    avail,
                    "drive-steering-wheel",
                    "Driving left",
                    "driving-progress-bar",
                    availabilityArray.indexOf(avail / HOURS)
                ));
            }

            let checkWorkday = () => rulesetConf.workday && this.isPresent(rulesetConf.workday.workdayMaximum) && this.isPresent(availability.workday);
            if (workdayContainer && checkWorkday()) {
                let avail = availability.workday && timeSpanToMs(availability.workday) || 0;
                let getWorkdayLimit = (rulesetConfig: DeepReadonly<IHosRulesetConfig>, workdayParams: IWorkdaySummary | undefined): number => {
                    let workdayLimit = rulesetConfig.workday && rulesetConfig.workday.workdayMaximum || 0;
                    if (workdayParams && workdayParams.workdayExtension) {
                        workdayLimit += timeSpanToMs(workdayParams.workdayExtension);
                    }
                    return workdayLimit;
                };

                this.progressBarCollection.push(this.showLockProgressbar(
                    workdayContainer,
                    ringMax(getWorkdayLimit(rulesetConf, workdayInfo), avail),
                    avail,
                    "drive-tools",
                    "Workday left",
                    "workday-progress-bar",
                    availabilityArray.indexOf(avail / HOURS)
                ));
            }

            let checkCycle = () => rulesetConf.cycle && this.isPresent(rulesetConf.cycle.cycleMaximum) && this.isPresent(availability.cycle);
            if (cycleContainer && checkCycle()) {
                let avail = availability.cycle && timeSpanToMs(availability.cycle) || 0;
                let getCycleLimit = rulesetConfig => rulesetConfig.cycle && rulesetConfig.cycle.cycleMaximum || 0;

                this.progressBarCollection.push(this.showLockProgressbar(
                    cycleContainer,
                    ringMax(getCycleLimit(rulesetConf), avail),
                    avail,
                    "drive-ccw",
                    "Cycle left",
                    "cycle-progress-bar",
                    availabilityArray.indexOf(avail / HOURS)
                ));
            }
        }

        private getConfigForPeriod(config: IEwdRulesetWorkRestConfig[], timePeriod: number) {
            if (config) {
                for (let i = 0; i < config.length; i++) {
                    if (config[i].timePeriod === timePeriod) {
                        return config[i];
                    }
                }
            }
            return null;
        }

        private showEwdAvailability () {
            if (!this.ruleResult || (this.ruleResult.ewdAvailability || []).length === 0) {
                return;
            }

            let availability = this.ruleResult.ewdAvailability as IEwdWorkRestAvailability[];
            let userRuleSetLabel = this.hosUtils.getValidRuleSet(this.hosUtils.getLatestHosRuleSetIdShortened(true));
            let rulesetConf = this.hosUtils.getRuleSets()[userRuleSetLabel].config.workRestConfig as IEwdRulesetWorkRestConfig[];

            let shortBreakContainer = document.getElementById("shortBreakLockBar");
            let longBreakContainer = document.getElementById("longBreakLockBar");
            let oneDayRestContainer = document.getElementById("oneDayRestLockBar");
            let oneNightRestContainer = document.getElementById("oneNightRestLockBar");
            let twoNightRestContainer = document.getElementById("twoNightRestLockBar");

            let shortBreakRestTime = { min: Infinity, max: 0 };
            let longBreakRestTime = { min: Infinity, max: 0 };
            let oneDayRestTime = { min: Infinity, max: 0 };
            let nightRestBreakCount = -1;
            let consecutiveNightRestBreakCount = -1;
            let multiNightRestTime = { min: Infinity, max: 0 };

            availability.forEach(ewdPeriod => {
                let timeUntilMustRestMs = timeSpanToMs(ewdPeriod.timeUntilMustRest);
                let config = this.getConfigForPeriod(rulesetConf, ewdPeriod.timePeriod);
                let restBreak = config?.restPeriod;
                let workLimit = config?.workPeriod;
                if (restBreak && workLimit && workLimit.workMaximum) {
                    if (restBreak.restMinimum && ewdPeriod.timeUntilMustRest !== undefined) {
                        if (restBreak.restMinimum === FIFTEEN_MINUTES) {
                            if (timeUntilMustRestMs < shortBreakRestTime.min) {
                                shortBreakRestTime.min = timeUntilMustRestMs;
                                shortBreakRestTime.max = workLimit.workMaximum;
                            }
                        } else if (restBreak.restMinimum === SEVEN_HOURS) {
                            if (timeUntilMustRestMs < longBreakRestTime.min) {
                                longBreakRestTime.min = timeUntilMustRestMs;
                                longBreakRestTime.max = workLimit.workMaximum;
                            }
                        } else if (restBreak.restMinimum === TWENTYFOUR_HOURS) {
                            if (timeUntilMustRestMs < oneDayRestTime.min) {
                                oneDayRestTime.min = timeUntilMustRestMs;
                                oneDayRestTime.max = workLimit.workMaximum;
                            }
                        }
                    } else if (restBreak.consecutiveNightRestBreakCount || restBreak.nightRestBreakCount) {
                        if (restBreak.nightRestBreakCount && (!ewdPeriod.nightRestBreakCount || ewdPeriod.nightRestBreakCount < restBreak.nightRestBreakCount)) {
                            nightRestBreakCount = ewdPeriod.nightRestBreakCount || 0;
                        }

                        if (restBreak.consecutiveNightRestBreakCount
                            && (!ewdPeriod.consecutiveNightRestBreakCount || ewdPeriod.consecutiveNightRestBreakCount < restBreak.consecutiveNightRestBreakCount)) {
                                consecutiveNightRestBreakCount = ewdPeriod.consecutiveNightRestBreakCount || 0;
                        }

                        if (ewdPeriod.timeUntilMustRest !== undefined && timeUntilMustRestMs < multiNightRestTime.min) {
                            multiNightRestTime.min = timeUntilMustRestMs;
                            multiNightRestTime.max = workLimit.workMaximum;
                        }
                    }
                }
            });

            if (shortBreakContainer && shortBreakRestTime.min !== Infinity) {
                this.progressBarCollection.push(this.showLockProgressbar(
                    shortBreakContainer,
                    shortBreakRestTime.max,
                    shortBreakRestTime.min,
                    "",
                    this.getString("{0} break in").replace("{0}", `15${ this.getString("{minutes}m").replace("{minutes}", "") }`),
                    "short-break-progress-bar",
                    0,
                    true
                ));
            }

            if (longBreakContainer && longBreakRestTime.min !== Infinity) {
                this.progressBarCollection.push(this.showLockProgressbar(
                    longBreakContainer,
                    longBreakRestTime.max,
                    longBreakRestTime.min,
                    "",
                    this.getString("{0} rest in").replace("{0}", `${ 7 }${ this.getString("h") }`),
                    "long-break-progress-bar",
                    1,
                    true
                ));
            }

            if (oneDayRestContainer && oneDayRestTime.min !== Infinity) {
                this.progressBarCollection.push(this.showLockProgressbar(
                    oneDayRestContainer,
                    oneDayRestTime.max,
                    oneDayRestTime.min,
                    "",
                    this.getString("{0} rest in").replace("{0}", `${ 24 }${ this.getString("h") }`),
                    "one-day-rest-progress-bar",
                    2,
                    true
                ));
            }

            if (multiNightRestTime.min !== Infinity) {
                if (oneNightRestContainer && nightRestBreakCount > -1) {
                    this.progressBarCollection.push(this.showLockProgressbar(
                        oneNightRestContainer,
                        multiNightRestTime.max,
                        multiNightRestTime.min,
                        "",
                        this.getString("{0} rest in").replace("{0}", this.getString("{0}-night").replace("{0}", "1")),
                        "one-night-rest-progress-bar",
                        3,
                        true
                    ));
                }

                if (twoNightRestContainer && consecutiveNightRestBreakCount > -1) {
                    this.progressBarCollection.push(this.showLockProgressbar(
                        twoNightRestContainer,
                        multiNightRestTime.max,
                        multiNightRestTime.min,
                        "",
                        this.getString("{0} rest in").replace("{0}", this.getString("{0}-night").replace("{0}", "2")),
                        "two-night-rest-progress-bar",
                        4,
                        true
                    ));
                }
            }
        }
    }